import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { 
  Shield, Users, CheckCircle, XCircle, Clock, 
  Eye, BadgeCheck, AlertTriangle, Loader2, 
  ChevronDown, ChevronUp, ExternalLink, Trash2, Database, Wrench,
  TrendingUp, DollarSign, BarChart3, Activity, Globe, Star, ShoppingBag, Calendar, MapPin, Lock
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import AdminToolsPanel from '@/components/AdminToolsPanel';
import { analyticsApi } from '@/api';

// Use current origin in production, env variable in development
const getApiUrl = () => {
  if (typeof window !== 'undefined' && window.location.origin.includes('xperience-guides.com')) {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || window.location.origin;
};
const API_URL = getApiUrl();

const AdminPage = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Admin password protection
  const ADMIN_PASSWORD = "XPerience2024!";
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  
  const [pendingVerifications, setPendingVerifications] = useState([]);
  const [allGuides, setAllGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalGuides: 0,
    verifiedGuides: 0,
    pendingVerifications: 0
  });
  const [demoStatus, setDemoStatus] = useState(null);
  const [clearingDemo, setClearingDemo] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsPeriod, setAnalyticsPeriod] = useState('week');

  // Check if user is admin (you can customize this logic)
  const isAdmin = user?.email === 'admin@xperience.com' || user?.email === 'steve.rosset1982@gmail.com' || user?.is_admin;

  // Check session storage for unlock status
  useEffect(() => {
    const unlocked = sessionStorage.getItem('admin_unlocked');
    if (unlocked === 'true') {
      setIsUnlocked(true);
    }
  }, []);

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (passwordInput === ADMIN_PASSWORD) {
      setIsUnlocked(true);
      setPasswordError(false);
      sessionStorage.setItem('admin_unlocked', 'true');
    } else {
      setPasswordError(true);
      setPasswordInput('');
    }
  };

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchData();
    fetchAnalytics(analyticsPeriod);
  }, [user, navigate]);

  useEffect(() => {
    if (user) {
      fetchAnalytics(analyticsPeriod);
    }
  }, [analyticsPeriod]);

  const fetchAnalytics = async (period = 'week') => {
    setAnalyticsLoading(true);
    try {
      const days = period === 'day' ? 1 : period === 'week' ? 7 : period === 'month' ? 30 : 365;
      const data = await analyticsApi.getOverview(days);
      setAnalytics(data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('vgt_token');
      
      // Fetch pending verifications
      const pendingRes = await fetch(`${API_URL}/api/admin/pending-verifications`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (pendingRes.ok) {
        const pendingData = await pendingRes.json();
        setPendingVerifications(pendingData.pending || []);
      }

      // Fetch all guides for stats (use admin endpoint for full info)
      const guidesRes = await fetch(`${API_URL}/api/admin/explorers?limit=100`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (guidesRes.ok) {
        const guidesData = await guidesRes.json();
        // API returns array directly
        const guides = Array.isArray(guidesData) ? guidesData : (guidesData.explorers || []);
        setAllGuides(guides);
        
        // Recalculate stats with fresh pending data
        const pendingCount = pendingVerifications.length;
        setStats({
          totalGuides: guides.filter(g => !g.is_demo).length, // Only count real guides
          verifiedGuides: guides.filter(g => g.verified && !g.is_demo).length,
          pendingVerifications: pendingCount
        });
      }

      // Fetch demo data status
      const demoRes = await fetch(`${API_URL}/api/admin/demo-data-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (demoRes.ok) {
        const demoData = await demoRes.json();
        setDemoStatus(demoData);
      }
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error(i18n.language === 'fr' ? 'Erreur de chargement' : 'Loading error');
    } finally {
      setLoading(false);
    }
  };

  const handleVerification = async (userId, approved, rejectionReason = null) => {
    try {
      const token = localStorage.getItem('vgt_token');
      const url = `${API_URL}/api/admin/verify-guide/${userId}?approved=${approved}${rejectionReason ? `&rejection_reason=${encodeURIComponent(rejectionReason)}` : ''}`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success(approved 
          ? (i18n.language === 'fr' ? 'Guide vérifié !' : 'Guide verified!')
          : (i18n.language === 'fr' ? 'Vérification rejetée' : 'Verification rejected')
        );
        fetchData();
      } else {
        throw new Error('Failed');
      }
    } catch (error) {
      toast.error(i18n.language === 'fr' ? 'Erreur' : 'Error');
    }
  };

  const handleClearDemoData = async () => {
    if (!window.confirm(i18n.language === 'fr' 
      ? 'Êtes-vous sûr de vouloir supprimer toutes les données de démonstration ? Cette action est irréversible.'
      : 'Are you sure you want to delete all demo data? This action cannot be undone.'
    )) return;

    setClearingDemo(true);
    try {
      const token = localStorage.getItem('vgt_token');
      const response = await fetch(`${API_URL}/api/admin/clear-demo-data`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(i18n.language === 'fr' 
          ? `Données de démo supprimées : ${result.users_deleted} guides, ${result.experiences_deleted} expériences`
          : `Demo data cleared: ${result.users_deleted} guides, ${result.experiences_deleted} experiences`
        );
        fetchData();
      } else {
        throw new Error('Failed');
      }
    } catch (error) {
      toast.error(i18n.language === 'fr' ? 'Erreur lors de la suppression' : 'Error clearing data');
    } finally {
      setClearingDemo(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Password protection screen
  if (!isUnlocked) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-2xl font-bold">
              {i18n.language === 'fr' ? 'Accès Administration' : 'Admin Access'}
            </h1>
            <p className="text-muted-foreground mt-2">
              {i18n.language === 'fr' ? 'Entrez le mot de passe administrateur' : 'Enter the admin password'}
            </p>
          </div>
          
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <div>
              <input
                type="password"
                value={passwordInput}
                onChange={(e) => setPasswordInput(e.target.value)}
                placeholder={i18n.language === 'fr' ? 'Mot de passe...' : 'Password...'}
                className={`w-full px-4 py-3 rounded-lg border ${passwordError ? 'border-red-500 bg-red-50' : 'border-gray-200'} focus:outline-none focus:ring-2 focus:ring-primary`}
                autoFocus
              />
              {passwordError && (
                <p className="text-red-500 text-sm mt-2">
                  {i18n.language === 'fr' ? 'Mot de passe incorrect' : 'Incorrect password'}
                </p>
              )}
            </div>
            <button
              type="submit"
              className="w-full bg-primary text-white py-3 rounded-lg font-semibold hover:bg-primary/90 transition-colors"
            >
              {i18n.language === 'fr' ? 'Accéder' : 'Access'}
            </button>
          </form>
          
          <div className="mt-6 text-center">
            <button
              onClick={() => navigate('/')}
              className="text-muted-foreground hover:text-primary text-sm"
            >
              {i18n.language === 'fr' ? '← Retour au site' : '← Back to site'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
              <Shield className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">
                {i18n.language === 'fr' ? 'Administration' : 'Administration'}
              </h1>
              <p className="text-muted-foreground">
                {i18n.language === 'fr' ? 'Gestion de la plateforme XPerience' : 'XPerience platform management'}
              </p>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl p-6 shadow-sm border"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  {i18n.language === 'fr' ? 'Total Guides' : 'Total Guides'}
                </p>
                <p className="text-2xl font-bold">{stats.totalGuides}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl p-6 shadow-sm border"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <BadgeCheck className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  {i18n.language === 'fr' ? 'Guides Vérifiés' : 'Verified Guides'}
                </p>
                <p className="text-2xl font-bold">{stats.verifiedGuides}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl p-6 shadow-sm border"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  {i18n.language === 'fr' ? 'En Attente' : 'Pending'}
                </p>
                <p className="text-2xl font-bold">{pendingVerifications.length}</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Main Content */}
        <Tabs defaultValue="pending" className="space-y-6">
          <TabsList className="bg-white border">
            <TabsTrigger value="pending" className="data-[state=active]:bg-primary data-[state=active]:text-white">
              <Clock className="w-4 h-4 mr-2" />
              {i18n.language === 'fr' ? 'Vérifications en attente' : 'Pending Verifications'}
              {pendingVerifications.length > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full text-xs">
                  {pendingVerifications.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="guides" className="data-[state=active]:bg-primary data-[state=active]:text-white">
              <Users className="w-4 h-4 mr-2" />
              {i18n.language === 'fr' ? 'Tous les guides' : 'All Guides'}
            </TabsTrigger>
            <TabsTrigger value="demo" className="data-[state=active]:bg-primary data-[state=active]:text-white">
              <Database className="w-4 h-4 mr-2" />
              {i18n.language === 'fr' ? 'Données démo' : 'Demo Data'}
              {demoStatus?.demo_users > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full text-xs">
                  {demoStatus.demo_users}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="tools" className="data-[state=active]:bg-primary data-[state=active]:text-white">
              <Wrench className="w-4 h-4 mr-2" />
              {i18n.language === 'fr' ? 'Outils' : 'Tools'}
            </TabsTrigger>
            <TabsTrigger value="analytics" className="data-[state=active]:bg-primary data-[state=active]:text-white">
              <BarChart3 className="w-4 h-4 mr-2" />
              {i18n.language === 'fr' ? 'Analytics' : 'Analytics'}
            </TabsTrigger>
          </TabsList>

          {/* Pending Verifications Tab */}
          <TabsContent value="pending">
            <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
              {pendingVerifications.length === 0 ? (
                <div className="p-12 text-center">
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2">
                    {i18n.language === 'fr' ? 'Aucune demande en attente' : 'No pending requests'}
                  </h3>
                  <p className="text-muted-foreground">
                    {i18n.language === 'fr' 
                      ? 'Toutes les demandes de vérification ont été traitées.'
                      : 'All verification requests have been processed.'}
                  </p>
                </div>
              ) : (
                <div className="divide-y">
                  {pendingVerifications.map((guide) => (
                    <VerificationCard 
                      key={guide.user_id} 
                      guide={guide} 
                      onVerify={handleVerification}
                    />
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* All Guides Tab */}
          <TabsContent value="guides">
            <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">
                        {i18n.language === 'fr' ? 'Guide' : 'Guide'}
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">
                        {i18n.language === 'fr' ? 'Email' : 'Email'}
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">
                        {i18n.language === 'fr' ? 'Statut' : 'Status'}
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">
                        {i18n.language === 'fr' ? 'Expériences' : 'Experiences'}
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">
                        {i18n.language === 'fr' ? 'Note' : 'Rating'}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {allGuides.map((guide) => (
                      <tr key={guide.user_id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <img 
                              src={guide.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(guide.name)}&background=F97316&color=fff`}
                              alt={guide.name}
                              className="w-10 h-10 rounded-full object-cover"
                            />
                            <div>
                              <p className="font-medium flex items-center gap-1">
                                {guide.name}
                                {guide.verified && <BadgeCheck className="w-4 h-4 text-blue-500" />}
                                {guide.is_demo && (
                                  <span className="ml-1 px-1.5 py-0.5 bg-orange-100 text-orange-600 rounded text-xs">
                                    DEMO
                                  </span>
                                )}
                              </p>
                              <p className="text-sm text-muted-foreground">{guide.location}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">{guide.email}</td>
                        <td className="px-6 py-4">
                          {guide.verified ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                              <BadgeCheck className="w-3 h-3" />
                              {i18n.language === 'fr' ? 'Vérifié' : 'Verified'}
                            </span>
                          ) : guide.verification_requested ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">
                              <Clock className="w-3 h-3" />
                              {i18n.language === 'fr' ? 'En attente' : 'Pending'}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
                              {i18n.language === 'fr' ? 'Non vérifié' : 'Not verified'}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm">{guide.experience_count || 0}</td>
                        <td className="px-6 py-4">
                          {guide.rating > 0 ? (
                            <div className="flex items-center gap-1">
                              <span className="text-yellow-500">★</span>
                              <span className="text-sm font-medium">{guide.rating?.toFixed(1)}</span>
                            </div>
                          ) : (
                            <span className="text-sm text-muted-foreground">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Demo Data Tab */}
          <TabsContent value="demo">
            <div className="bg-white rounded-2xl shadow-sm border p-6">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                  <Database className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">
                    {i18n.language === 'fr' ? 'Gestion des données de démonstration' : 'Demo Data Management'}
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    {i18n.language === 'fr' 
                      ? 'Les données de démo sont automatiquement supprimées lorsque suffisamment de vrais guides s\'inscrivent.'
                      : 'Demo data is automatically deleted when enough real guides register.'}
                  </p>
                </div>
              </div>

              {demoStatus && (
                <div className="space-y-6">
                  {/* Status Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 bg-gray-50 rounded-xl">
                      <p className="text-sm text-muted-foreground">
                        {i18n.language === 'fr' ? 'Guides de démo' : 'Demo Guides'}
                      </p>
                      <p className="text-2xl font-bold text-orange-600">{demoStatus.demo_users}</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-xl">
                      <p className="text-sm text-muted-foreground">
                        {i18n.language === 'fr' ? 'Expériences de démo' : 'Demo Experiences'}
                      </p>
                      <p className="text-2xl font-bold text-orange-600">{demoStatus.demo_experiences}</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-xl">
                      <p className="text-sm text-muted-foreground">
                        {i18n.language === 'fr' ? 'Vrais guides inscrits' : 'Real Guides Registered'}
                      </p>
                      <p className="text-2xl font-bold text-green-600">{demoStatus.real_guides}</p>
                    </div>
                  </div>

                  {/* Auto-cleanup Status */}
                  <div className={`p-4 rounded-xl border ${demoStatus.will_auto_cleanup ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200'}`}>
                    <div className="flex items-start gap-3">
                      {demoStatus.will_auto_cleanup ? (
                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                      ) : (
                        <Clock className="w-5 h-5 text-blue-600 mt-0.5" />
                      )}
                      <div>
                        <p className="font-medium">
                          {demoStatus.will_auto_cleanup 
                            ? (i18n.language === 'fr' ? 'Seuil atteint !' : 'Threshold reached!')
                            : (i18n.language === 'fr' ? 'Nettoyage automatique en attente' : 'Auto-cleanup pending')}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {demoStatus.will_auto_cleanup 
                            ? (i18n.language === 'fr' 
                                ? 'Les données de démo seront supprimées à la prochaine inscription de guide.'
                                : 'Demo data will be deleted on the next guide registration.')
                            : (i18n.language === 'fr' 
                                ? `Encore ${demoStatus.remaining_until_cleanup} guide(s) avant la suppression automatique (seuil: ${demoStatus.cleanup_threshold}).`
                                : `${demoStatus.remaining_until_cleanup} more guide(s) until auto-cleanup (threshold: ${demoStatus.cleanup_threshold}).`)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Manual Clear Button */}
                  {demoStatus.demo_users > 0 && (
                    <div className="flex justify-end">
                      <Button
                        variant="outline"
                        onClick={handleClearDemoData}
                        disabled={clearingDemo}
                        className="text-red-600 border-red-200 hover:bg-red-50"
                      >
                        {clearingDemo ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4 mr-2" />
                        )}
                        {i18n.language === 'fr' ? 'Supprimer maintenant' : 'Clear Now'}
                      </Button>
                    </div>
                  )}

                  {/* No Demo Data */}
                  {demoStatus.demo_users === 0 && (
                    <div className="text-center py-8">
                      <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                      <p className="font-medium">
                        {i18n.language === 'fr' ? 'Aucune donnée de démonstration' : 'No demo data'}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {i18n.language === 'fr' 
                          ? 'Toutes les données sont des données réelles d\'utilisateurs.'
                          : 'All data is real user data.'}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Admin Tools Tab */}
          <TabsContent value="tools">
            <div className="bg-white rounded-2xl shadow-sm border p-6">
              <AdminToolsPanel />
            </div>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <AnalyticsDashboard 
              analytics={analytics} 
              loading={analyticsLoading} 
              onRefresh={() => fetchAnalytics(analyticsPeriod)}
              period={analyticsPeriod}
              onPeriodChange={setAnalyticsPeriod}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

// Verification Card Component
const VerificationCard = ({ guide, onVerify }) => {
  const { i18n } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [loading, setLoading] = useState(false);

  const handleApprove = async () => {
    setLoading(true);
    await onVerify(guide.user_id, true);
    setLoading(false);
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast.error(i18n.language === 'fr' ? 'Veuillez indiquer une raison' : 'Please provide a reason');
      return;
    }
    setLoading(true);
    await onVerify(guide.user_id, false, rejectionReason);
    setLoading(false);
    setShowRejectModal(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="p-6"
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <img 
            src={guide.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(guide.name)}&background=F97316&color=fff&size=80`}
            alt={guide.name}
            className="w-16 h-16 rounded-xl object-cover"
          />
          <div>
            <h3 className="font-semibold text-lg">{guide.name}</h3>
            <p className="text-sm text-muted-foreground">{guide.email}</p>
            <p className="text-sm text-muted-foreground">{guide.location}</p>
            <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
              <Clock className="w-3 h-3" />
              {i18n.language === 'fr' ? 'Demandé le' : 'Requested on'}: {' '}
              {new Date(guide.verification_requested_at).toLocaleDateString()}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            {i18n.language === 'fr' ? 'Détails' : 'Details'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowRejectModal(true)}
            className="text-red-600 border-red-200 hover:bg-red-50"
          >
            <XCircle className="w-4 h-4 mr-1" />
            {i18n.language === 'fr' ? 'Rejeter' : 'Reject'}
          </Button>
          <Button
            size="sm"
            onClick={handleApprove}
            disabled={loading}
            className="bg-green-600 hover:bg-green-700"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <CheckCircle className="w-4 h-4 mr-1" />
                {i18n.language === 'fr' ? 'Approuver' : 'Approve'}
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Expanded Details */}
      {expanded && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          className="mt-6 p-4 bg-gray-50 rounded-xl"
        >
          <h4 className="font-medium mb-3">
            {i18n.language === 'fr' ? 'Document soumis' : 'Submitted Document'}
          </h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground mb-1">
                {i18n.language === 'fr' ? 'Type de document' : 'Document Type'}
              </p>
              <p className="font-medium capitalize">{guide.verification_document_type || 'ID Card'}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">
                {i18n.language === 'fr' ? 'Lien du document' : 'Document Link'}
              </p>
              {guide.verification_document_url ? (
                <a 
                  href={guide.verification_document_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary hover:underline flex items-center gap-1"
                >
                  {i18n.language === 'fr' ? 'Voir le document' : 'View Document'}
                  <ExternalLink className="w-3 h-3" />
                </a>
              ) : (
                <span className="text-muted-foreground">
                  {i18n.language === 'fr' ? 'Non fourni' : 'Not provided'}
                </span>
              )}
            </div>
          </div>
          
          {guide.bio && (
            <div className="mt-4">
              <p className="text-sm text-muted-foreground mb-1">Bio</p>
              <p className="text-sm">{guide.bio}</p>
            </div>
          )}
        </motion.div>
      )}

      {/* Reject Modal */}
      <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              {i18n.language === 'fr' ? 'Rejeter la vérification' : 'Reject Verification'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {i18n.language === 'fr' 
                ? `Vous êtes sur le point de rejeter la demande de vérification de ${guide.name}.`
                : `You are about to reject the verification request from ${guide.name}.`}
            </p>
            <div>
              <label className="text-sm font-medium">
                {i18n.language === 'fr' ? 'Raison du rejet *' : 'Rejection Reason *'}
              </label>
              <Textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder={i18n.language === 'fr' 
                  ? 'Document illisible, informations incorrectes...'
                  : 'Unreadable document, incorrect information...'}
                rows={3}
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowRejectModal(false)}>
                {i18n.language === 'fr' ? 'Annuler' : 'Cancel'}
              </Button>
              <Button 
                onClick={handleReject}
                disabled={loading}
                className="bg-red-600 hover:bg-red-700"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <XCircle className="w-4 h-4 mr-1" />
                    {i18n.language === 'fr' ? 'Confirmer le rejet' : 'Confirm Rejection'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};

// Analytics Dashboard Component
const AnalyticsDashboard = ({ analytics, loading, onRefresh, period, onPeriodChange }) => {
  const { i18n } = useTranslation();
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [chartType, setChartType] = useState('bar'); // 'bar' or 'line'

  const periods = [
    { id: 'day', label: i18n.language === 'fr' ? 'Jour' : 'Day', days: 1 },
    { id: 'week', label: i18n.language === 'fr' ? 'Semaine' : 'Week', days: 7 },
    { id: 'month', label: i18n.language === 'fr' ? 'Mois' : 'Month', days: 30 },
    { id: 'year', label: i18n.language === 'fr' ? 'Année' : 'Year', days: 365 },
  ];

  const currentPeriod = periods.find(p => p.id === period) || periods[1];

  // Auto-refresh every 30 seconds when enabled
  useEffect(() => {
    let interval;
    if (autoRefresh) {
      interval = setInterval(() => {
        onRefresh();
      }, 30000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh, onRefresh]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        {i18n.language === 'fr' ? 'Erreur de chargement des analytics' : 'Error loading analytics'}
      </div>
    );
  }

  const StatCard = ({ icon: Icon, label, value, subValue, color = "blue", trend }) => (
    <div className="bg-white rounded-xl border p-5 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className={`w-12 h-12 rounded-xl bg-${color}-100 flex items-center justify-center`}>
          <Icon className={`w-6 h-6 text-${color}-600`} />
        </div>
        {trend && (
          <span className={`text-xs font-medium px-2 py-1 rounded-full ${
            trend > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
          }`}>
            {trend > 0 ? '+' : ''}{trend}%
          </span>
        )}
      </div>
      <p className="text-2xl font-bold mt-3">{value}</p>
      <p className="text-sm text-muted-foreground">{label}</p>
      {subValue && <p className="text-xs text-muted-foreground mt-1">{subValue}</p>}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header with Period Selector */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-bold">
            {i18n.language === 'fr' ? 'Tableau de bord Analytics' : 'Analytics Dashboard'}
          </h2>
          {autoRefresh && (
            <span className="flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
              {i18n.language === 'fr' ? 'Temps réel' : 'Live'}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Period Selector */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            {periods.map((p) => (
              <button
                key={p.id}
                onClick={() => onPeriodChange(p.id)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                  period === p.id
                    ? 'bg-white text-primary shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                data-testid={`period-${p.id}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          
          {/* Auto-refresh toggle */}
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
              autoRefresh 
                ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            data-testid="auto-refresh-toggle"
          >
            <Activity className={`w-4 h-4 ${autoRefresh ? 'animate-pulse' : ''}`} />
            {i18n.language === 'fr' ? 'Auto' : 'Auto'}
          </button>
          
          <Button onClick={onRefresh} variant="outline" size="sm" disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Activity className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {/* Traffic Overview */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-6 text-white">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Globe className="w-5 h-5" />
          {i18n.language === 'fr' ? `Trafic du site (${currentPeriod.label})` : `Site Traffic (${currentPeriod.label})`}
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white/10 rounded-xl p-4">
            <p className="text-3xl font-bold">{analytics.traffic?.views_today || 0}</p>
            <p className="text-sm text-white/80">{i18n.language === 'fr' ? "Vues aujourd'hui" : 'Views today'}</p>
          </div>
          <div className="bg-white/10 rounded-xl p-4">
            <p className="text-3xl font-bold">{analytics.traffic?.unique_today || 0}</p>
            <p className="text-sm text-white/80">{i18n.language === 'fr' ? "Visiteurs uniques (aujourd'hui)" : 'Unique visitors (today)'}</p>
          </div>
          <div className="bg-white/10 rounded-xl p-4">
            <p className="text-3xl font-bold">{analytics.traffic?.views_period || 0}</p>
            <p className="text-sm text-white/80">{i18n.language === 'fr' ? `Vues (${currentPeriod.label})` : `Views (${currentPeriod.label})`}</p>
          </div>
          <div className="bg-white/10 rounded-xl p-4">
            <p className="text-3xl font-bold">{analytics.traffic?.unique_period || 0}</p>
            <p className="text-sm text-white/80">{i18n.language === 'fr' ? `Visiteurs uniques (${currentPeriod.label})` : `Unique visitors (${currentPeriod.label})`}</p>
          </div>
        </div>
      </div>

      {/* Main Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard 
          icon={Users} 
          label={i18n.language === 'fr' ? "Total utilisateurs" : "Total users"}
          value={analytics.users?.total || 0}
          subValue={`+${analytics.users?.new_this_month || 0} ${i18n.language === 'fr' ? "ce mois" : "this month"}`}
          color="blue"
        />
        <StatCard 
          icon={BadgeCheck} 
          label={i18n.language === 'fr' ? "Guides" : "Guides"}
          value={analytics.users?.guides || 0}
          subValue={`${analytics.users?.verified_guides || 0} ${i18n.language === 'fr' ? "vérifiés" : "verified"}`}
          color="green"
        />
        <StatCard 
          icon={Star} 
          label={i18n.language === 'fr' ? "Expériences actives" : "Active experiences"}
          value={analytics.experiences?.total_active || 0}
          subValue={`Note moy: ${analytics.experiences?.average_rating || 0}⭐`}
          color="orange"
        />
        <StatCard 
          icon={ShoppingBag} 
          label={i18n.language === 'fr' ? "Réservations" : "Bookings"}
          value={analytics.bookings?.total || 0}
          subValue={`${analytics.bookings?.confirmed || 0} ${i18n.language === 'fr' ? "confirmées" : "confirmed"}`}
          color="purple"
        />
      </div>

      {/* Revenue & Bookings */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border p-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-green-600" />
            {i18n.language === 'fr' ? 'Revenus' : 'Revenue'}
          </h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
              <span className="text-muted-foreground">{i18n.language === 'fr' ? 'Revenu total' : 'Total revenue'}</span>
              <span className="font-bold text-lg">{(analytics.revenue?.total / 100 || 0).toFixed(2)}€</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
              <span className="text-muted-foreground">{i18n.language === 'fr' ? 'Commission plateforme' : 'Platform commission'}</span>
              <span className="font-bold text-lg text-green-600">{(analytics.revenue?.commission / 100 || 0).toFixed(2)}€</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
              <span className="text-muted-foreground">{i18n.language === 'fr' ? 'Ce mois' : 'This month'}</span>
              <span className="font-bold text-lg text-blue-600">{(analytics.revenue?.this_month / 100 || 0).toFixed(2)}€</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border p-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-purple-600" />
            {i18n.language === 'fr' ? 'Activité ce mois' : 'This month activity'}
          </h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
              <span className="text-muted-foreground">{i18n.language === 'fr' ? 'Nouveaux utilisateurs' : 'New users'}</span>
              <span className="font-bold text-lg">{analytics.users?.new_this_month || 0}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
              <span className="text-muted-foreground">{i18n.language === 'fr' ? 'Nouveaux guides' : 'New guides'}</span>
              <span className="font-bold text-lg">{analytics.users?.new_guides_month || 0}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
              <span className="text-muted-foreground">{i18n.language === 'fr' ? 'Nouvelles expériences' : 'New experiences'}</span>
              <span className="font-bold text-lg">{analytics.experiences?.new_this_month || 0}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
              <span className="text-muted-foreground">{i18n.language === 'fr' ? 'Réservations' : 'Bookings'}</span>
              <span className="font-bold text-lg text-purple-600">{analytics.bookings?.this_month || 0}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Popular Content */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border p-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-orange-600" />
            {i18n.language === 'fr' ? `Pages populaires (${currentPeriod.label})` : `Popular pages (${currentPeriod.label})`}
          </h3>
          <div className="space-y-2">
            {analytics.popular?.pages?.length > 0 ? (
              analytics.popular.pages.slice(0, 6).map((page, idx) => (
                <div key={idx} className="flex justify-between items-center p-2 hover:bg-gray-50 rounded-lg">
                  <span className="text-sm truncate flex-1">{page._id || '/'}</span>
                  <span className="font-medium text-sm text-muted-foreground">{page.views} vues</span>
                </div>
              ))
            ) : (
              <p className="text-muted-foreground text-sm">{i18n.language === 'fr' ? 'Pas encore de données' : 'No data yet'}</p>
            )}
          </div>
        </div>

        <div className="bg-white rounded-2xl border p-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Star className="w-5 h-5 text-yellow-600" />
            {i18n.language === 'fr' ? `Expériences populaires (${currentPeriod.label})` : `Popular experiences (${currentPeriod.label})`}
          </h3>
          <div className="space-y-2">
            {analytics.popular?.experiences?.length > 0 ? (
              analytics.popular.experiences.map((exp, idx) => (
                <div key={idx} className="flex justify-between items-center p-2 hover:bg-gray-50 rounded-lg">
                  <span className="text-sm truncate flex-1">{exp.title}</span>
                  <span className="font-medium text-sm text-muted-foreground">{exp.views} vues</span>
                </div>
              ))
            ) : (
              <p className="text-muted-foreground text-sm">{i18n.language === 'fr' ? 'Pas encore de données' : 'No data yet'}</p>
            )}
          </div>
        </div>
      </div>

      {/* Daily Traffic Chart - Interactive */}
      <div className="bg-white rounded-2xl border p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold flex items-center gap-2">
            <Activity className="w-5 h-5 text-blue-600" />
            {i18n.language === 'fr' ? `Trafic (${currentPeriod.label})` : `Traffic (${currentPeriod.label})`}
          </h3>
          <div className="flex items-center gap-2">
            {/* Chart type selector */}
            <div className="flex bg-gray-100 rounded-lg p-0.5">
              <button
                onClick={() => setChartType('bar')}
                className={`p-1.5 rounded-md transition-all ${chartType === 'bar' ? 'bg-white shadow-sm' : ''}`}
                title={i18n.language === 'fr' ? 'Barres' : 'Bars'}
              >
                <BarChart3 className="w-4 h-4" />
              </button>
              <button
                onClick={() => setChartType('line')}
                className={`p-1.5 rounded-md transition-all ${chartType === 'line' ? 'bg-white shadow-sm' : ''}`}
                title={i18n.language === 'fr' ? 'Ligne' : 'Line'}
              >
                <TrendingUp className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
        
        {analytics.traffic?.daily?.length > 0 ? (
          <TrafficChart 
            data={analytics.traffic.daily} 
            chartType={chartType}
            period={period}
          />
        ) : (
          <div className="h-48 flex items-center justify-center text-muted-foreground">
            {i18n.language === 'fr' ? 'Pas encore de données de trafic' : 'No traffic data yet'}
          </div>
        )}
      </div>

      {/* Geographic Stats */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border p-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Globe className="w-5 h-5 text-green-600" />
            {i18n.language === 'fr' ? `Visiteurs par pays (${currentPeriod.label})` : `Visitors by country (${currentPeriod.label})`}
          </h3>
          <div className="space-y-2">
            {analytics.geography?.by_country?.length > 0 ? (
              analytics.geography.by_country.map((item, idx) => (
                <div key={idx} className="flex justify-between items-center p-2 hover:bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <span className="text-xl">{getCountryFlag(item.country_code)}</span>
                    <span className="text-sm">{item.country}</span>
                  </div>
                  <span className="font-medium text-sm bg-green-100 text-green-700 px-2 py-1 rounded-full">
                    {item.unique_visitors} {i18n.language === 'fr' ? 'visiteurs' : 'visitors'}
                  </span>
                </div>
              ))
            ) : (
              <p className="text-muted-foreground text-sm">
                {i18n.language === 'fr' ? 'Les données géographiques apparaîtront avec les prochaines visites' : 'Geographic data will appear with upcoming visits'}
              </p>
            )}
          </div>
        </div>

        <div className="bg-white rounded-2xl border p-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-red-600" />
            {i18n.language === 'fr' ? `Top villes (${currentPeriod.label})` : `Top cities (${currentPeriod.label})`}
          </h3>
          <div className="space-y-2">
            {analytics.geography?.by_city?.length > 0 ? (
              analytics.geography.by_city.map((item, idx) => (
                <div key={idx} className="flex justify-between items-center p-2 hover:bg-gray-50 rounded-lg">
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">{item.city}</span>
                    <span className="text-xs text-muted-foreground">{item.country}</span>
                  </div>
                  <span className="font-medium text-sm bg-red-100 text-red-700 px-2 py-1 rounded-full">
                    {item.unique_visitors}
                  </span>
                </div>
              ))
            ) : (
              <p className="text-muted-foreground text-sm">
                {i18n.language === 'fr' ? 'Les données de villes apparaîtront avec les prochaines visites' : 'City data will appear with upcoming visits'}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper function to get country flag emoji from country code
const getCountryFlag = (countryCode) => {
  if (!countryCode) return '🌍';
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
};

// Interactive Traffic Chart Component
const TrafficChart = ({ data, chartType, period }) => {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  
  // Limit data points based on period
  const maxPoints = period === 'day' ? 24 : period === 'week' ? 7 : period === 'month' ? 30 : 12;
  const chartData = data.slice(-maxPoints);
  
  if (chartData.length === 0) return null;
  
  const maxViews = Math.max(...chartData.map(d => d.views), 1);
  const chartHeight = 200;
  const chartWidth = 100; // percentage
  const padding = { top: 20, bottom: 30, left: 10, right: 10 };
  
  // Calculate points for line chart
  const getX = (index) => ((index / (chartData.length - 1 || 1)) * (chartWidth - padding.left - padding.right)) + padding.left;
  const getY = (views) => chartHeight - padding.bottom - ((views / maxViews) * (chartHeight - padding.top - padding.bottom));
  
  // Format date label based on period
  const formatLabel = (dateStr) => {
    if (!dateStr) return '';
    if (period === 'year') {
      // Show month name
      const parts = dateStr.split('-');
      const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
      return months[parseInt(parts[1]) - 1] || dateStr.slice(5);
    }
    return dateStr.slice(5); // MM-DD
  };

  return (
    <div className="relative" style={{ height: chartHeight }}>
      {/* Y-axis labels */}
      <div className="absolute left-0 top-0 bottom-8 w-10 flex flex-col justify-between text-xs text-gray-400">
        <span>{maxViews}</span>
        <span>{Math.round(maxViews / 2)}</span>
        <span>0</span>
      </div>
      
      {/* Chart area */}
      <div className="ml-10 h-full relative">
        {/* Grid lines */}
        <div className="absolute inset-0 flex flex-col justify-between pointer-events-none" style={{ paddingTop: padding.top, paddingBottom: padding.bottom }}>
          {[0, 1, 2].map((i) => (
            <div key={i} className="border-t border-gray-100 w-full" />
          ))}
        </div>
        
        {chartType === 'bar' ? (
          /* Bar Chart */
          <div className="absolute inset-0 flex items-end gap-1" style={{ paddingTop: padding.top, paddingBottom: padding.bottom }}>
            {chartData.map((day, idx) => {
              const height = (day.views / maxViews) * 100;
              const isHovered = hoveredIndex === idx;
              return (
                <div 
                  key={idx} 
                  className="flex-1 flex flex-col items-center justify-end h-full relative group"
                  onMouseEnter={() => setHoveredIndex(idx)}
                  onMouseLeave={() => setHoveredIndex(null)}
                >
                  {/* Tooltip */}
                  {isHovered && (
                    <div className="absolute bottom-full mb-2 bg-gray-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-10">
                      {day.views} vues - {formatLabel(day._id)}
                    </div>
                  )}
                  
                  {/* Bar */}
                  <div 
                    className={`w-full rounded-t-sm transition-all duration-200 ${
                      isHovered 
                        ? 'bg-gradient-to-t from-blue-600 to-indigo-500' 
                        : 'bg-gradient-to-t from-blue-500 to-blue-400'
                    }`}
                    style={{ 
                      height: `${Math.max(height, 2)}%`,
                      transform: isHovered ? 'scaleY(1.05)' : 'scaleY(1)',
                      transformOrigin: 'bottom'
                    }}
                  />
                </div>
              );
            })}
          </div>
        ) : (
          /* Line Chart */
          <svg className="absolute inset-0 w-full h-full" viewBox={`0 0 100 ${chartHeight}`} preserveAspectRatio="none">
            {/* Area fill */}
            <defs>
              <linearGradient id="areaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.3" />
                <stop offset="100%" stopColor="#3B82F6" stopOpacity="0.05" />
              </linearGradient>
            </defs>
            
            {/* Area path */}
            <path
              d={`
                M ${getX(0)} ${chartHeight - padding.bottom}
                ${chartData.map((d, i) => `L ${getX(i)} ${getY(d.views)}`).join(' ')}
                L ${getX(chartData.length - 1)} ${chartHeight - padding.bottom}
                Z
              `}
              fill="url(#areaGradient)"
            />
            
            {/* Line path */}
            <path
              d={`M ${chartData.map((d, i) => `${getX(i)} ${getY(d.views)}`).join(' L ')}`}
              fill="none"
              stroke="#3B82F6"
              strokeWidth="0.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            
            {/* Data points */}
            {chartData.map((day, idx) => (
              <g key={idx}>
                <circle
                  cx={getX(idx)}
                  cy={getY(day.views)}
                  r={hoveredIndex === idx ? "1.5" : "0.8"}
                  fill={hoveredIndex === idx ? "#1D4ED8" : "#3B82F6"}
                  className="transition-all duration-200"
                  onMouseEnter={() => setHoveredIndex(idx)}
                  onMouseLeave={() => setHoveredIndex(null)}
                  style={{ cursor: 'pointer' }}
                />
              </g>
            ))}
          </svg>
        )}
        
        {/* X-axis labels */}
        <div className="absolute bottom-0 left-0 right-0 flex justify-between text-xs text-gray-400 h-6 items-end">
          {chartData.filter((_, i) => {
            // Show fewer labels for longer periods
            if (chartData.length <= 7) return true;
            if (chartData.length <= 14) return i % 2 === 0;
            if (chartData.length <= 30) return i % 5 === 0;
            return i % 3 === 0;
          }).map((day, idx) => (
            <span key={idx} className="text-center" style={{ minWidth: '30px' }}>
              {formatLabel(day._id)}
            </span>
          ))}
        </div>
        
        {/* Hover tooltip for line chart */}
        {chartType === 'line' && hoveredIndex !== null && chartData[hoveredIndex] && (
          <div 
            className="absolute bg-gray-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-10 pointer-events-none"
            style={{
              left: `${(hoveredIndex / (chartData.length - 1 || 1)) * 100}%`,
              top: `${(1 - chartData[hoveredIndex].views / maxViews) * (chartHeight - padding.top - padding.bottom) + padding.top - 30}px`,
              transform: 'translateX(-50%)'
            }}
          >
            {chartData[hoveredIndex].views} vues - {formatLabel(chartData[hoveredIndex]._id)}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
